/**
 * THE COLOUR A SWATCH IS PAINTED (Swe Sosetta run 5, round C: S fix round 2, #2, 2026-10-28).
 *
 * A COLOR value with no photo of its own draws its swatch (fix round 1, #2), and the swatch was painted from the value's
 * NAME — `resolveColorPreview` knows a dozen English names and whatever CSS knows. «أبيض», «שחור» or "Rose Gold" are
 * none of those, so the disc came out empty. The builder writes each colour's hex beside it (`valueMeta.hex`, as run 3's
 * cake pops carry «أبيض» #c3bd96), so that is read first:
 *   1. the value's own hex — `mainVariants[].valueMeta.hex` on the product page's payload, `swatches[].valueMeta.hex` on a
 *      card payload (and the choice's own value meta, `axisValueMeta`, where a payload carries those);
 *   2. else the name, when CSS knows it as a colour (the storefront's presets first: black → #000000 …);
 *   3. else nothing — and the caller draws a neutral ring with the value's first letter, never an empty disc.
 *
 * THE ONE PLACE A COLOUR NAME BECOMES PAINT (S round 3). Every swatch the storefront paints — the product page's tiles
 * and one-value dot, the option sheet, the full-screen viewer, the shop filter's colour chips — comes through here, and
 * no other file maps a colour NAME to a paint value (`aColourNameBecomesPaintInOnePlace.guard.test.js`).
 */
import { SLOTS, slotChoice } from './shopperText';

/** The storefront's own words for a dozen colours (an English name a shop typed) → the paint it uses for them. */
const PRESET_PAINT = {
  black: '#000000', white: '#ffffff', gray: '#9ca3af', beige: '#d6c5a0',
  red: '#dc2626', pink: '#ec4899', orange: '#f97316', yellow: '#facc15',
  green: '#16a34a', blue: '#2563eb', purple: '#7c3aed', brown: '#78350f',
};

const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const hexOf = (meta) => {
  const hex = meta && typeof meta === 'object' && typeof meta.hex === 'string' ? meta.hex.trim() : '';
  return HEX.test(hex) ? hex : null;
};

/** A colour NAME as CSS paints it: a preset's hex, else the name when CSS knows it as a colour; null when it does not. */
export function namedColour(name) {
  const raw = typeof name === 'string' ? name.trim() : '';
  if (!raw) return null;
  const preset = PRESET_PAINT[raw.toLowerCase()];
  if (preset) return preset;
  if (HEX.test(raw)) return raw;
  if (typeof document === 'undefined') return null;
  try {
    const probe = document.createElement('span').style;
    probe.color = raw;
    return probe.color ? raw : null;
  } catch (_) {
    return null;
  }
}

/** The hex a payload gives a value — its card's, a card payload's swatch's, its choice's, `axisValueMeta` — or null. */
export function swatchHex(product, slot, value) {
  if (value == null || value === '' || !product || typeof product !== 'object') return null;
  const raw = String(value);
  if (SLOTS.indexOf(slot) === 0) {
    for (const list of [product.mainVariants, product.swatches]) {
      const hit = (Array.isArray(list) ? list : []).find((m) => m && m.value === raw && hexOf(m.valueMeta));
      if (hit) return hexOf(hit.valueMeta);
    }
  }
  const fromChoice = (slotChoice(product, slot)?.values || []).find((v) => v && v.value === raw && hexOf(v.meta));
  if (fromChoice) return hexOf(fromChoice.meta);
  const carded = product.axisValueMeta && typeof product.axisValueMeta === 'object' ? product.axisValueMeta[slot] : null;
  return carded ? hexOf(carded[raw]) : null;
}

/** The paint for a swatch that already knows its hex (or none) and its name: the hex, else the name, else null. */
export function swatchPaint({ hex, name } = {}) {
  return hexOf({ hex }) || namedColour(name);
}

/** The colour a value's swatch is painted, or null when the payload gives none and CSS knows no colour by its name. */
export function swatchColour(product, slot, value) {
  if (value == null || value === '') return null;
  return swatchPaint({ hex: swatchHex(product, slot, value), name: String(value) });
}

/** The letter a neutral ring carries: the first character of the value as the shopper reads it. */
export function swatchLetter(label) {
  const first = Array.from(String(label ?? '').trim())[0];
  return first ? first.toLocaleUpperCase() : '';
}
